import { useState, useEffect } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import ContactSalesForm from "@/components/ContactSalesForm";
import { 
  ArrowLeft, 
  RefreshCw,
  Download,
  ExternalLink,
  CheckCircle2,
  Calculator,
  Phone,
  Star
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecommendationPage = () => {
  const { category } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);

  const answers = location.state?.answers || {};

  useEffect(() => {
    if (Object.keys(answers).length === 0) {
      navigate(`/questionnaire/${category}`);
      return;
    }
    fetchRecommendation();
  }, [category, answers]);

  const fetchRecommendation = async () => {
    try {
      const response = await axios.post(`${API}/recommend`, {
        category,
        answers
      });
      setRecommendation(response.data);
    } catch (error) {
      console.error("Error fetching recommendation:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (productId, productName) => {
    try {
      const response = await axios.get(`${API}/products/${productId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${productName.replace(/\s+/g, '_')}_Spec_Sheet.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const handleStartOver = () => {
    navigate("/");
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  const primary = recommendation?.primary;
  const alternative = recommendation?.alternative;

  return (
    <Layout>
      <div className="py-8 md:py-12" data-testid="recommendation-page">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <Link 
                to={`/questionnaire/${category}`}
                className="inline-flex items-center text-[#00BCD4] hover:text-[#00acc1] font-medium mb-3"
                data-testid="back-to-questions"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Questions
              </Link>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-800 tracking-tight">
                Recommended Products
              </h1>
            </div>
            <Button 
              onClick={handleStartOver}
              
              className="border-slate-300 bg-[#00BCD4] hover:bg-[#00acc1]"
              data-testid="start-over-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Start Over
            </Button>
          </div>

          {/* Reasoning */}
          {recommendation?.reasoning && (
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-8 animate-fadeIn">
              <p className="text-slate-700">{recommendation.reasoning}</p>
            </div>
          )}

          {!primary ? (
            <div className="text-center py-12">
              <p className="text-xl text-slate-600 mb-4">No matching products found for your criteria.</p>
              <Button onClick={handleStartOver} className="bg-[#00BCD4] hover:bg-[#00acc1]">
                Try Again
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Primary Recommendation */}
              <div className="recommendation-card animate-fadeIn" data-testid="primary-recommendation">
                <div className="bg-[#00BCD4] text-white px-6 py-3 flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  <span className="font-bold uppercase tracking-wide text-sm">Primary Recommendation</span>
                </div>
                
                <div className="p-6 md:p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left: Product Info */}
                    <div>
                      <span className="text-xs font-bold tracking-wider text-[#00BCD4] uppercase mb-2 block">
                        {primary.category}
                      </span>
                      <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">
                        {primary.name}
                      </h2>
                      <p className="text-slate-600 mb-6 leading-relaxed">
                        {primary.description}
                      </p>

                      {/* Dosage */}
                      <div className="bg-slate-50 rounded-lg p-4 mb-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xs font-bold tracking-wider text-slate-400 uppercase">
                              Recommended Dosage
                            </span>
                            <p className="text-2xl font-mono font-bold text-slate-800">
                              {primary.dosage_range}
                            </p>
                          </div>
                          <Link to="/calculator">
                            <Button size="sm" className="bg-[#00BCD4] hover:bg-[#00acc1]">
                              <Calculator className="w-4 h-4 mr-1" />
                              Calculate
                            </Button>
                          </Link>
                        </div>
                      </div>

                      {/* Applications */}
                      <div className="mb-6">
                        <span className="text-xs font-bold tracking-wider text-slate-400 uppercase block mb-2">
                          Applications
                        </span>
                        <div className="flex flex-wrap">
                          {primary.applications?.map((app, i) => (
                            <span key={i} className="app-tag">{app}</span>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-3">
                        {/* <Button 
                          onClick={() => handleDownloadPDF(primary.id, primary.name)}
                          className="bg-[#00BCD4] hover:bg-[#00acc1]"
                          data-testid="download-primary-pdf"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Spec Sheet
                        </Button> */}
                        {primary.product_url && (
                          <a href={primary.product_url} target="_blank" rel="noopener noreferrer">
                            <Button className="bg-[#00BCD4] hover:bg-[#00acc1]">
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Product Page
                            </Button>
                          </a>
                        )}
                        <ContactSalesForm 
                          productInterest={primary.name}
                          trigger={
                            <Button className="bg-[#00BCD4] hover:bg-[#00acc1]">
                              <Phone className="w-4 h-4 mr-2" />
                              Contact Sales
                            </Button>
                          }
                        />
                      </div>
                    </div>

                    {/* Right: Features & Specs */}
                    <div>
                      {/* Features */}
                      <div className="mb-6">
                        <span className="text-xs font-bold tracking-wider text-slate-400 uppercase block mb-3">
                          Key Features
                        </span>
                        <ul className="space-y-2">
                          {primary.features?.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-700">
                              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Specifications */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="text-xs font-bold tracking-wider text-slate-400 uppercase block mb-3">
                          Technical Specifications
                        </span>
                        <table className="spec-table">
                          <tbody>
                            {Object.entries(primary.specifications || {}).map(([key, value]) => (
                              <tr key={key}>
                                <th>{key}</th>
                                <td>{value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Alternative Recommendation */}
              {alternative && (
                <div className="recommendation-card alternative animate-fadeIn stagger-2 mt-8" data-testid="alternative-recommendation">
                  <div className="bg-slate-600 text-white px-6 py-3">
                    <span className="font-bold uppercase tracking-wide text-sm">Alternative Option</span>
                  </div>
                  
                  <div className="p-6 md:p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">
                          {alternative.name}
                        </h3>
                        <p className="text-slate-600 mb-4">
                          {alternative.description}
                        </p>
                        <div className="flex items-center gap-4 mb-4">
                          <span className="text-sm text-slate-500">Dosage:</span>
                          <span className="font-mono font-semibold text-slate-800">{alternative.dosage_range}</span>
                        </div>
                        <div className="flex gap-2">
                          {/* <Button 
                            onClick={() => handleDownloadPDF(alternative.id, alternative.name)}
                            variant="outline"
                            size="sm"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Spec Sheet
                          </Button> */}
                          <Link to={`/products/${alternative.id}`}>
                            <Button size="sm" className="w-full bg-[#00BCD4] hover:bg-[#00acc1]">
                              View Details
                            </Button>
                          </Link>
                        </div>
                      </div>
                      <div>
                        <ul className="space-y-2">
                          {alternative.features?.slice(0, 4).map((feature, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RecommendationPage;
